import React, { useEffect, useState } from 'react';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrency } from '../../contexts/CurrencyContext';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed'];

const statusLabels = {
  pending: 'En attente',
  approved: 'Approuvé',
  disbursed: 'Décaissé',
  active: 'Actif',
  completed: 'Terminé',
  defaulted: 'Défaillant',
};

const Analytics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();
  const { formatCurrency } = useCurrency();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const data = await tenantService.getTenantPerformanceMetrics();
        setMetrics(data);
      } catch (error) {
        showError('Erreur lors du chargement des analytiques');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [showError]);

  const monthlyClients = metrics?.monthly_clients || [];
  const loansByStatus = (metrics?.loans_by_status || []).map((item) => ({
    ...item,
    label: statusLabels[item.status] || item.status,
  }));
  const agencyPerformance = metrics?.agency_performance || [];
  const totalClients = monthlyClients.reduce((sum, item) => sum + (item.clients || 0), 0);
  const activeLoanStatuses = loansByStatus.reduce((sum, item) => sum + (item.count || 0), 0);
  const totalSavings = agencyPerformance.reduce((sum, item) => sum + (item.savings || 0), 0);
  const totalLoans = agencyPerformance.reduce((sum, item) => sum + (item.loans || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des analytiques...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Analytiques</h1>
        <p className="mt-1 text-sm text-gray-600">
          Indicateurs de performance du tenant, des agences et du portefeuille.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['Clients ajoutés', totalClients, UsersIcon, 'bg-blue-50 text-blue-600'],
          ['Prêts suivis', activeLoanStatuses, ChartBarIcon, 'bg-violet-50 text-violet-600'],
          ['Épargne agences', formatCurrency(totalSavings), BuildingOfficeIcon, 'bg-green-50 text-green-600'],
          ['Prêts agences', formatCurrency(totalLoans), CurrencyDollarIcon, 'bg-amber-50 text-amber-600'],
        ].map(([title, value, Icon, color]) => (
          <div key={title} className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-3 ${color}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{value}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Clients par mois</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyClients}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="clients" name="Clients" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Prêts par statut</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={loansByStatus} dataKey="count" nameKey="label" outerRadius={95} label>
                    {loansByStatus.map((entry, index) => (
                      <Cell key={entry.status} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Épargne vs prêts par agence</h3>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={agencyPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agency" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="savings" name="Épargne" fill="#16a34a" />
                <Bar dataKey="loans" name="Prêts" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
